/* eslint-disable no-nested-ternary */
import { useAuth } from '@context';
import { useAtomValue } from 'jotai';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import Button from '@components/NewButton/Button';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { MdAddCircle, MdCheck } from 'react-icons/md';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Enable2FAForm } from '@forms/Enable2FAForm/Enable2FAForm';
import { Modal, ModalContainer, ModalHeader } from '@components/Modal';
import {
  ShieldCheckIcon,
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { PhoneNumberInput } from '../../tailwindComponents/PhoneNumberInput';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import {
  BUSINESS_PROFILE_CREATE,
  BUSINESS_PROFILE_UPDATE,
  COUNTRY_CODES,
} from '../../constants';
import { useAuthHttp } from '../../hooks';
import { businessProfileAtom } from '../../store/jotai';
import FinanceForm from './FinanceForm';
import { NotificationPreferencesCard } from './NotificationPreferencesCard';
import { UserRoleService } from '../../services';
import {
  GOODS_PALLETISED_OPTIONS,
  OUTBOUND_UNIT_OPTIONS,
  STORAGE_UNIT_OPTIONS,
  goodsPalletisedAnswerFrom,
  palletisedGoodsPayload,
  selectedOption,
} from '../../utils/businessNeeds';

const schema = Yup.object({
  businessNumber: Yup.string().required('Company number is required'),
  businessAddress: Yup.string().required('Registered address is required'),
  vatNumber: Yup.string().required('VAT number is required'),
  businessIndustry: Yup.string().nullable(),
  businessWebsite: Yup.string().nullable(),
  businessContactName: Yup.string().required('Contact name is required'),
  country: Yup.string().required('Please select a country.'),
  businessContactPhoneNumber: Yup.string()
    .matches(/^[0-9]{6,14}$/, {
      message: 'Please enter a valid mobile phone number',
      excludeEmptyString: true,
    })
    .required('Business contact phone number is required'),
  businessContactEmail: Yup.string()
    .email('Please enter a valid email address')
    .required('Business contact email is required'),
  groupContactEmail: Yup.string().email('Please enter a valid email address'),
});

export type UpdateBusinessProfileFormValues = {
  businessName: string;
  businessNumber: string;
  businessAddress: string;
  vatNumber: string;
  businessIndustry: string;
  businessWebsite: string;
  businessContactName: string;
  country: string;
  businessContactPhoneNumber: string;
  businessContactEmail: string;
  groupContactEmail?: string;
  // Initial Business Volume (shipper, FinanceAdmin only)
  initialStorageQuantityPerWeek?: number | null;
  initialDurationInMonths?: number | null;
  initialInboundShipmentsPerWeek?: number | null;
  initialOutboundShipmentsPerWeek?: number | null;
  requiredAccreditations?: string;
  sustainabilityRequirements?: string;
  temperatureRequirements?: string;
  productType?: string;
  productDetailRequirements?: string;
  approxNumberOfSkus?: number | null;
  averageNumberOfOrderlines?: number | null;
  additionalServices?: string;
  initialStorageRequirementUnitOfMeasure?: string;
  desiredStartDate?: string;
  goodsPalletised?: string;
  palletWidthInMetres?: number | null;
  palletLengthInMetres?: number | null;
  palletHeightInMetres?: number | null;
  palletWeightInKg?: number | null;
  initialOutboundUnitsPerWeek?: number | null;
  initialOutboundUnitOfMeasure?: string;
  // Systems in use (LSP)
  tmsProvider?: string;
  wmsProvider?: string;
};

export type BusinessProfile = {
  businessProfile: {
    id: string;
    businessType: string;
    businessSize: string;
    contactName: string;
    targetCustomerType: string;
    numberOfPalletsInAndOutPerWeek: number;
    averageShipmentSizePerWeek: number;
    averageShipmentType: string;
    teamSize: string;
    address: string;
    companyNumber: string;
    industry: string;
    website: string;
    contactEmail: string;
    groupContactEmail?: string;
    contactPhoneNumber: {
      countryCode: 'string';
      countryNumberPrefix: 'string';
      number: 'string';
    };
    hasConnectedBankAccount: boolean;
    areFinancialFeaturesDisabled: boolean;
    vatNumber: string;
    initialStorageQuantityPerWeek?: number;
    initialDurationInMonths?: number;
    initialInboundShipmentsPerWeek?: number;
    initialOutboundShipmentsPerWeek?: number;
    requiredAccreditations?: string;
    sustainabilityRequirements?: string;
    temperatureRequirements?: string;
    productType?: string;
    productDetailRequirements?: string;
    approxNumberOfSkus?: number;
    averageNumberOfOrderlines?: number;
    additionalServices?: string;
    initialStorageRequirementUnitOfMeasure?: string;
    desiredStartDate?: string;
    goodsPalletised?: boolean;
    palletWidthInMetres?: number;
    palletLengthInMetres?: number;
    palletHeightInMetres?: number;
    palletWeightInKg?: number;
    initialOutboundUnitsPerWeek?: number;
    initialOutboundUnitOfMeasure?: string;
    tmsProvider?: string;
    wmsProvider?: string;
    warehouseTandCAgreement: {
      hasAgreed: boolean;
      agreedById: string;
      agreedByEmailAddress: string;
      agreedByName: string;
      agreementDate: string;
    };
  };
};

export function Profile() {
  const { user } = useAuth();
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isWarehouseClient } = UserRoleService();
  const [
    isUpdateBusinessProfileFormSubmitting,
    setIsUpdateBusinessProfileFormSubmitting,
  ] = useState(false);
  const [
    hasUpdateBusinessProfileFormSubmitted,
    setHasUpdateBusinessProfileFormSubmitted,
  ] = useState(false);
  const businessProfile = useAtomValue(businessProfileAtom);
  const [isBusinessProfileComplete, setIsBusinessProfileComplete] = useState(
    !!businessProfile?.companyNumber &&
      !!businessProfile?.address &&
      !!businessProfile?.contactEmail &&
      !!businessProfile?.contactName
  );
  const [is2faModalOpen, setIs2faModalOpen] = useState(false);
  const [haveSetUp2Fa, setHaveSetUp2Fa] = useState(
    user?.twoFactorMethodIds !== undefined && user.twoFactorMethodIds.length > 0
  );

  const warehouseIDParam = searchParams.get('warehouseID') || '';

  const defaultValues = {
    businessName: user?.businessName || '',
    businessNumber: businessProfile?.companyNumber || '',
    businessAddress: businessProfile?.address || '',
    businessIndustry: businessProfile?.industry || '',
    businessWebsite: businessProfile?.website || '',
    businessContactName: businessProfile?.contactName || '',
    country: businessProfile?.contactPhoneNumber?.countryCode || 'GB',
    businessContactPhoneNumber:
      businessProfile?.contactPhoneNumber?.number || '',
    businessContactEmail: businessProfile?.contactEmail || '',
    groupContactEmail: businessProfile?.groupContactEmail || '',
    vatNumber: businessProfile?.vatNumber || '',
    initialStorageQuantityPerWeek: businessProfile?.initialStorageQuantityPerWeek ?? null,
    initialDurationInMonths: businessProfile?.initialDurationInMonths ?? null,
    initialInboundShipmentsPerWeek: businessProfile?.initialInboundShipmentsPerWeek ?? null,
    initialOutboundShipmentsPerWeek: businessProfile?.initialOutboundShipmentsPerWeek ?? null,
    requiredAccreditations: businessProfile?.requiredAccreditations || '',
    sustainabilityRequirements: businessProfile?.sustainabilityRequirements || '',
    temperatureRequirements: businessProfile?.temperatureRequirements || '',
    productType: businessProfile?.productType || '',
    productDetailRequirements: businessProfile?.productDetailRequirements || '',
    approxNumberOfSkus: businessProfile?.approxNumberOfSkus ?? null,
    averageNumberOfOrderlines: businessProfile?.averageNumberOfOrderlines ?? null,
    additionalServices: businessProfile?.additionalServices || '',
    initialStorageRequirementUnitOfMeasure:
      businessProfile?.initialStorageRequirementUnitOfMeasure || '',
    desiredStartDate: businessProfile?.desiredStartDate
      ? businessProfile.desiredStartDate.slice(0, 10)
      : '',
    goodsPalletised: goodsPalletisedAnswerFrom(businessProfile?.goodsPalletised),
    palletWidthInMetres: businessProfile?.palletWidthInMetres ?? null,
    palletLengthInMetres: businessProfile?.palletLengthInMetres ?? null,
    palletHeightInMetres: businessProfile?.palletHeightInMetres ?? null,
    palletWeightInKg: businessProfile?.palletWeightInKg ?? null,
    initialOutboundUnitsPerWeek:
      businessProfile?.initialOutboundUnitsPerWeek ?? null,
    initialOutboundUnitOfMeasure:
      businessProfile?.initialOutboundUnitOfMeasure || '',
    tmsProvider: businessProfile?.tmsProvider || '',
    wmsProvider: businessProfile?.wmsProvider || '',
  };

  const methods = useForm<UpdateBusinessProfileFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: { ...defaultValues },
  });
  const { handleSubmit, control, reset } = methods;

  console.log(methods.formState.errors);

  const handleUpdateBusinessProfileFormSubmit = async (
    values: UpdateBusinessProfileFormValues
  ) => {
    setIsUpdateBusinessProfileFormSubmitting(true);
    try {
      const { data } = await authHttp.post<BusinessProfile>(
        businessProfile ? BUSINESS_PROFILE_UPDATE : BUSINESS_PROFILE_CREATE,
        {
          businessProfile: {
            address: values.businessAddress,
            companyNumber: values.businessNumber,
            industry: values.businessIndustry,
            website: values.businessWebsite,
            contactName: values.businessContactName,
            contactEmail: values.businessContactEmail,
            groupContactEmail: values.groupContactEmail,
            contactPhoneNumber: {
              countryCode: COUNTRY_CODES.find(
                (country) => country.country_code === values.country
              )?.country_code,
              countryNumberPrefix: COUNTRY_CODES.find(
                (country) => country.country_code === values.country
              )?.phone_code.toString(),
              number: values.businessContactPhoneNumber,
            },
            vatNumber: values.vatNumber,
            initialStorageQuantityPerWeek: values.initialStorageQuantityPerWeek || null,
            initialDurationInMonths: values.initialDurationInMonths || null,
            initialInboundShipmentsPerWeek: values.initialInboundShipmentsPerWeek ?? null,
            initialOutboundShipmentsPerWeek: values.initialOutboundShipmentsPerWeek ?? null,
            requiredAccreditations: values.requiredAccreditations || null,
            sustainabilityRequirements: values.sustainabilityRequirements || null,
            temperatureRequirements: values.temperatureRequirements || null,
            productType: values.productType || null,
            productDetailRequirements: values.productDetailRequirements || null,
            approxNumberOfSkus: values.approxNumberOfSkus || null,
            averageNumberOfOrderlines: values.averageNumberOfOrderlines || null,
            additionalServices: values.additionalServices || null,
            initialStorageRequirementUnitOfMeasure:
              values.initialStorageRequirementUnitOfMeasure || null,
            desiredStartDate: values.desiredStartDate || null,
            ...palletisedGoodsPayload(values),
            initialOutboundUnitsPerWeek:
              values.initialOutboundUnitsPerWeek ?? null,
            initialOutboundUnitOfMeasure:
              values.initialOutboundUnitOfMeasure || null,
            tmsProvider: values.tmsProvider || null,
            wmsProvider: values.wmsProvider || null,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data) {
        setHasUpdateBusinessProfileFormSubmitted(true);
        if (warehouseIDParam) navigate(`/warehouse/${warehouseIDParam}`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdateBusinessProfileFormSubmitting(false);
    }
  };

  useEffect(() => {
    reset({
      businessNumber: businessProfile?.companyNumber || '',
      businessAddress: businessProfile?.address || '',
      businessIndustry: businessProfile?.industry || '',
      businessWebsite: businessProfile?.website || '',
      businessContactName: businessProfile?.contactName || '',
      country: businessProfile?.contactPhoneNumber?.countryCode || 'GB',
      businessContactPhoneNumber:
        businessProfile?.contactPhoneNumber?.number || '',
      businessContactEmail: businessProfile?.contactEmail || '',
      groupContactEmail: businessProfile?.groupContactEmail || '',
      vatNumber: businessProfile?.vatNumber || '',
      initialStorageQuantityPerWeek: businessProfile?.initialStorageQuantityPerWeek ?? null,
      initialDurationInMonths: businessProfile?.initialDurationInMonths ?? null,
      initialInboundShipmentsPerWeek: businessProfile?.initialInboundShipmentsPerWeek ?? null,
      initialOutboundShipmentsPerWeek: businessProfile?.initialOutboundShipmentsPerWeek ?? null,
      requiredAccreditations: businessProfile?.requiredAccreditations || '',
      sustainabilityRequirements: businessProfile?.sustainabilityRequirements || '',
      temperatureRequirements: businessProfile?.temperatureRequirements || '',
      productType: businessProfile?.productType || '',
      productDetailRequirements: businessProfile?.productDetailRequirements || '',
      approxNumberOfSkus: businessProfile?.approxNumberOfSkus ?? null,
      averageNumberOfOrderlines: businessProfile?.averageNumberOfOrderlines ?? null,
      additionalServices: businessProfile?.additionalServices || '',
      initialStorageRequirementUnitOfMeasure:
        businessProfile?.initialStorageRequirementUnitOfMeasure || '',
      desiredStartDate: businessProfile?.desiredStartDate
        ? businessProfile.desiredStartDate.slice(0, 10)
        : '',
      goodsPalletised: goodsPalletisedAnswerFrom(
        businessProfile?.goodsPalletised
      ),
      palletWidthInMetres: businessProfile?.palletWidthInMetres ?? null,
      palletLengthInMetres: businessProfile?.palletLengthInMetres ?? null,
      palletHeightInMetres: businessProfile?.palletHeightInMetres ?? null,
      palletWeightInKg: businessProfile?.palletWeightInKg ?? null,
      initialOutboundUnitsPerWeek:
        businessProfile?.initialOutboundUnitsPerWeek ?? null,
      initialOutboundUnitOfMeasure:
        businessProfile?.initialOutboundUnitOfMeasure || '',
      tmsProvider: businessProfile?.tmsProvider || '',
      wmsProvider: businessProfile?.wmsProvider || '',
    });
    setIsBusinessProfileComplete(
      !!businessProfile?.companyNumber &&
        !!businessProfile?.address &&
        !!businessProfile?.contactEmail &&
        !!businessProfile?.contactName
    );
  }, [businessProfile]);

  return (
    <>
      <PageHeader
        header='Account Profile'
        subHeader='Manage your personal information and business settings'
        pages={[{ name: 'Profile', href: '/profile', current: true }]}
      />
      <main className='flex flex-col gap-6 p-4 pb-16 lg:px-8 lg:pb-24'>
        {/* Personal Information Card */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
            <div className='flex items-center gap-3'>
              <UserIcon className='h-5 w-5 text-gray-400' />
              <h2 className='text-lg font-semibold text-gray-900'>
                Personal Information
              </h2>
            </div>
          </div>
          <div className='p-6'>
            <dl className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
              <div>
                <dt className='text-sm font-medium text-gray-500'>
                  Business Name
                </dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {user?.businessName || '—'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-gray-500'>Full Name</dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-gray-500 flex items-center gap-1.5'>
                  <EnvelopeIcon className='h-4 w-4' />
                  Email Address
                </dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {user?.email || '—'}
                </dd>
              </div>
              <div>
                <dt className='text-sm font-medium text-gray-500 flex items-center gap-1.5'>
                  <PhoneIcon className='h-4 w-4' />
                  Mobile Phone
                </dt>
                <dd className='mt-1 text-sm text-gray-900'>
                  {user?.mobilePhoneNumber || '—'}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Security Card */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
            <div className='flex items-center gap-3'>
              <ShieldCheckIcon className='h-5 w-5 text-gray-400' />
              <h2 className='text-lg font-semibold text-gray-900'>Security</h2>
            </div>
          </div>
          <div className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-sm font-medium text-gray-900'>
                  Two-Factor Authentication
                </h3>
                <p className='mt-1 text-sm text-gray-500'>
                  {haveSetUp2Fa
                    ? 'Two-factor authentication is enabled for this account'
                    : 'Add an extra layer of security to your account'}
                </p>
              </div>
              {haveSetUp2Fa ? (
                <span className='inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20'>
                  <ShieldCheckIcon className='h-4 w-4' />
                  Enabled
                </span>
              ) : (
                <Button
                  label='Enable 2FA'
                  onClick={() => setIs2faModalOpen(true)}
                  size='sm'
                />
              )}
            </div>
          </div>
        </div>

        {/* 2FA Modal */}
        <Modal
          open={is2faModalOpen}
          onOpenChange={(isOpen: boolean) => setIs2faModalOpen(isOpen)}
        >
          <ModalContainer preventCloseOnOutsideClick>
            <ModalHeader title='Enable 2 Factor Authentication' />
            <Enable2FAForm
              haveSetUp2Fa={haveSetUp2Fa}
              setHaveSetUp2Fa={setHaveSetUp2Fa}
            />
          </ModalContainer>
        </Modal>

        {/* Notification Preferences Card */}
        <NotificationPreferencesCard />

        {/* Business Profile Form */}
        {user?.roles.includes('BusinessUserManager') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
            <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
              <div className='flex items-center gap-3'>
                <BuildingOffice2Icon className='h-5 w-5 text-gray-400' />
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>
                    Business Profile
                  </h2>
                  <p className='mt-0.5 text-sm text-gray-500'>
                    This information will be used to confirm your
                    organisation&rsquo;s identity and to pre-populate any legal
                    agreements.
                  </p>
                </div>
              </div>
            </div>
            <div className='p-6'>
              <FormProvider {...methods}>
                <form
                  onSubmit={handleSubmit(handleUpdateBusinessProfileFormSubmit)}
                  noValidate
                  className='space-y-6'
                >
                  <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                    {/* Company Number */}
                    <Controller
                      name='businessNumber'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='Company Number'
                          name='businessNumber'
                          placeholder='Enter company number'
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />

                    {/* VAT Number */}
                    <Controller
                      name='vatNumber'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='VAT Number'
                          name='vatNumber'
                          placeholder='GB123 456 789'
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />

                    {/* Registered Address - Full Width */}
                    <Controller
                      name='businessAddress'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <div className='sm:col-span-2'>
                          <Input
                            {...field}
                            label='Registered Address'
                            name='businessAddress'
                            placeholder='Enter registered address'
                            isInvalid={!!error}
                            validationMessage={error?.message}
                          />
                        </div>
                      )}
                    />

                    {/* Company Industry */}
                    <Controller
                      name='businessIndustry'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='Company Industry'
                          name='businessIndustry'
                          placeholder='Enter industry'
                          optional
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />

                    {/* Corporate Website */}
                    <Controller
                      name='businessWebsite'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='Corporate Website'
                          name='businessWebsite'
                          placeholder='https://example.com'
                          optional
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />

                    {/* Contact Name */}
                    <Controller
                      name='businessContactName'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='Contact Name'
                          name='businessContactName'
                          placeholder='Enter contact name'
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />

                    {/* Phone Number with Country Code */}
                    <Controller
                      name='businessContactPhoneNumber'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <PhoneNumberInput
                          {...field}
                          label='Contact Phone Number'
                          name='businessContactPhoneNumber'
                          placeholder='Phone number'
                          countryCode={methods.watch('country') || 'GB'}
                          onCountryCodeChange={(code) =>
                            methods.setValue('country', code)
                          }
                          isInvalid={!!error}
                          validationMessage={error?.message}
                          disabled={isUpdateBusinessProfileFormSubmitting}
                        />
                      )}
                    />

                    {/* Corporate Contact Email */}
                    <Controller
                      name='businessContactEmail'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='Corporate Contact Email'
                          name='businessContactEmail'
                          type='email'
                          placeholder='contact@company.com'
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />

                    {/* Group Contact Email */}
                    <Controller
                      name='groupContactEmail'
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Input
                          {...field}
                          label='Group Contact Email'
                          name='groupContactEmail'
                          type='email'
                          placeholder='group@company.com'
                          optional
                          isInvalid={!!error}
                          validationMessage={error?.message}
                        />
                      )}
                    />
                  </div>

                  {/* Initial Business Volume — FinanceAdmin shippers only */}
                  {user?.roles.includes('FinanceAdmin') && isWarehouseClient(user) && (
                    <div className='border-t border-gray-200 pt-6'>
                      <h3 className='text-base font-semibold text-gray-900 mb-1'>
                        Initial Business Volume
                      </h3>
                      <p className='text-sm text-gray-500 mb-4'>
                        These values will pre-populate when you start a new warehouse engagement.
                      </p>
                      <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                        <Controller
                          name='initialStorageQuantityPerWeek'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Storage Needed (pallets/week)'
                              name='initialStorageQuantityPerWeek'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='initialStorageRequirementUnitOfMeasure'
                          control={control}
                          render={({ field }) => (
                            <Select
                              label='Storage Requirement Unit'
                              optional
                              placeholder='Select unit'
                              options={STORAGE_UNIT_OPTIONS}
                              selected={selectedOption(
                                STORAGE_UNIT_OPTIONS,
                                field.value
                              )}
                              setSelected={(option) =>
                                field.onChange(option.value)
                              }
                            />
                          )}
                        />
                        <Controller
                          name='initialDurationInMonths'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Estimated Duration (months)'
                              name='initialDurationInMonths'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='initialInboundShipmentsPerWeek'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Avg. Inbound Shipments (per week)'
                              name='initialInboundShipmentsPerWeek'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='initialOutboundShipmentsPerWeek'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Avg. Outbound Shipments (per week)'
                              name='initialOutboundShipmentsPerWeek'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='requiredAccreditations'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Required Accreditations'
                              name='requiredAccreditations'
                              placeholder='e.g. BRC, ISO 22000'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='sustainabilityRequirements'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Sustainability Requirements'
                              name='sustainabilityRequirements'
                              placeholder='e.g. carbon neutral, EV fleet'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='temperatureRequirements'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Temperature Requirements'
                              name='temperatureRequirements'
                              placeholder='e.g. ambient, chilled, frozen'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='productType'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Product Type'
                              name='productType'
                              placeholder='e.g. food, aerosols, cosmetics'
                              optional
                            />
                          )}
                        />
                        <div className='sm:col-span-2'>
                          <Controller
                            name='productDetailRequirements'
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                label='Product Detail Requirements'
                                name='productDetailRequirements'
                                placeholder="e.g. batch numbers, expiry dates"
                                optional
                              />
                            )}
                          />
                        </div>
                        <Controller
                          name='approxNumberOfSkus'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Approx. Number of SKUs'
                              name='approxNumberOfSkus'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='averageNumberOfOrderlines'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Avg. Number of Orderlines'
                              name='averageNumberOfOrderlines'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <div className='sm:col-span-2'>
                          <Controller
                            name='additionalServices'
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                label='Additional Services'
                                name='additionalServices'
                                placeholder='e.g. kitting, labelling, returns processing'
                                optional
                              />
                            )}
                          />
                        </div>
                        <Controller
                          name='desiredStartDate'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Desired Start Date'
                              name='desiredStartDate'
                              type='date'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='goodsPalletised'
                          control={control}
                          render={({ field }) => (
                            <Select
                              label='Goods Palletised'
                              optional
                              placeholder='Select'
                              options={GOODS_PALLETISED_OPTIONS}
                              selected={selectedOption(
                                GOODS_PALLETISED_OPTIONS,
                                field.value
                              )}
                              setSelected={(option) =>
                                field.onChange(option.value)
                              }
                            />
                          )}
                        />
                        {methods.watch('goodsPalletised') === 'Yes' && (
                          <>
                            <Controller
                              name='palletWidthInMetres'
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  value={field.value != null ? String(field.value) : ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                  label='Pallet Width (m)'
                                  name='palletWidthInMetres'
                                  type='number'
                                  step='0.1'
                                  placeholder='0'
                                  optional
                                />
                              )}
                            />
                            <Controller
                              name='palletLengthInMetres'
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  value={field.value != null ? String(field.value) : ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                  label='Pallet Length (m)'
                                  name='palletLengthInMetres'
                                  type='number'
                                  step='0.1'
                                  placeholder='0'
                                  optional
                                />
                              )}
                            />
                            <Controller
                              name='palletHeightInMetres'
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  value={field.value != null ? String(field.value) : ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                  label='Pallet Height (m)'
                                  name='palletHeightInMetres'
                                  type='number'
                                  step='0.1'
                                  placeholder='0'
                                  optional
                                />
                              )}
                            />
                            <Controller
                              name='palletWeightInKg'
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  value={field.value != null ? String(field.value) : ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                  label='Pallet Weight (kg)'
                                  name='palletWeightInKg'
                                  type='number'
                                  placeholder='0'
                                  optional
                                />
                              )}
                            />
                          </>
                        )}
                        <Controller
                          name='initialOutboundUnitsPerWeek'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                              label='Avg. Outbound Volume (UoM/week)'
                              name='initialOutboundUnitsPerWeek'
                              type='number'
                              placeholder='0'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='initialOutboundUnitOfMeasure'
                          control={control}
                          render={({ field }) => (
                            <Select
                              label='Outbound Unit of Measure'
                              optional
                              placeholder='Select unit'
                              options={OUTBOUND_UNIT_OPTIONS}
                              selected={selectedOption(
                                OUTBOUND_UNIT_OPTIONS,
                                field.value
                              )}
                              setSelected={(option) =>
                                field.onChange(option.value)
                              }
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Systems In Use — LSPs only */}
                  {!isWarehouseClient(user) && (
                    <div className='border-t border-gray-200 pt-6'>
                      <h3 className='text-base font-semibold text-gray-900 mb-1'>
                        Systems In Use
                      </h3>
                      <p className='text-sm text-gray-500 mb-4'>
                        Tell us which systems you run and we will prioritise
                        building integrations for them. This does not change
                        anything on your account.
                      </p>
                      <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                        <Controller
                          name='tmsProvider'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Transport Management System (TMS)'
                              name='tmsProvider'
                              placeholder='e.g. Mandata, Descartes'
                              optional
                            />
                          )}
                        />
                        <Controller
                          name='wmsProvider'
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              label='Warehouse Management System (WMS)'
                              name='wmsProvider'
                              placeholder='e.g. Ongoing, Peoplevox'
                              optional
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className='flex items-center justify-end gap-3 border-t border-gray-200 pt-6'>
                    {hasUpdateBusinessProfileFormSubmitted ? (
                      <Button
                        type='submit'
                        icon={<MdCheck size='1.25rem' />}
                        disabled={isUpdateBusinessProfileFormSubmitting}
                        size='sm'
                        intent='success'
                        label={
                          isUpdateBusinessProfileFormSubmitting
                            ? 'Submitting...'
                            : 'Verified'
                        }
                      />
                    ) : businessProfile && methods.formState.isDirty ? (
                      <Button
                        type='submit'
                        disabled={isUpdateBusinessProfileFormSubmitting}
                        size='sm'
                        intent='primary'
                        label={
                          isUpdateBusinessProfileFormSubmitting
                            ? 'Submitting...'
                            : 'Update'
                        }
                      />
                    ) : businessProfile && isBusinessProfileComplete ? (
                      <Button
                        type='submit'
                        icon={<MdCheck size='1.25rem' />}
                        disabled={isUpdateBusinessProfileFormSubmitting}
                        size='sm'
                        intent='success'
                        label={
                          isUpdateBusinessProfileFormSubmitting
                            ? 'Submitting...'
                            : 'Verified'
                        }
                      />
                    ) : (
                      <Button
                        type='submit'
                        icon={<MdAddCircle size='1.25rem' className='ml-1' />}
                        disabled={isUpdateBusinessProfileFormSubmitting}
                        size='sm'
                        intent='primary'
                        label={
                          isUpdateBusinessProfileFormSubmitting
                            ? 'Submitting...'
                            : 'Submit'
                        }
                      />
                    )}
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        )}

        {/* Finance Section */}
        {user?.roles.includes('FinanceAdmin') && (
          <>
            <FinanceForm
              hasConnectedBankAccount={businessProfile?.hasConnectedBankAccount}
              isShipper={isWarehouseClient(user)}
            />
            <p className='text-xs text-gray-500'>
              Yapily Connect Ltd, Authorised Payment Institution, FCA Registered
              827001.
            </p>
          </>
        )}
      </main>
    </>
  );
}
